import type {
  AdditionalSearchFilterGroup,
  AdditionalSearchFilters,
  MemberEntriesFilterValue,
  SearchFilters
} from '@library/types/members';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { DateOfBirthInput, Searchbar } from '@library/components/inputs';
import { memo, useCallback, useState } from 'react';
import { FormFieldController } from '@library/components/forms';
import { Fragment } from 'react/jsx-runtime';
import { MemberEntriesFilter } from '@bo-components/members/membersList';
import { PrizepicksDatePicker } from '@library/components/datePicker';

export interface MemberFiltersFormGroupProps {
  control: Control<SearchFilters>;
  filterGroup: AdditionalSearchFilterGroup;
  setValue: UseFormSetValue<SearchFilters>;
}

export const MemberFiltersFormGroup = memo(
  ({ control, filterGroup, setValue }: MemberFiltersFormGroupProps) => {
    const [joinDatePickerOpen, setJoinDatePickerOpen] = useState(false);

    const { groupName, filters } = filterGroup;
    const isLastGroup = groupName === 'Other search';

    const handleEntriesFilterChange = useCallback(
      (searchValue: MemberEntriesFilterValue) => {
        // Update both numberOfEntries and numberOfEntriesType
        setValue('numberOfEntries', searchValue.value ?? '');
        setValue('numberOfEntriesType', searchValue.type);
      },
      [setValue]
    );

    const renderFilterComponent = useCallback(
      (
        filter: AdditionalSearchFilters,
        value: string | Date | undefined,
        onChange: (value: string | Date | undefined) => void
      ) => {
        switch (filter.component) {
          case 'DateOfBirthInput':
            return (
              <DateOfBirthInput
                id="dateOfBirth"
                onValueChange={(searchValue) => onChange(searchValue)}
                showSearchIcon
                value={(value as string) ?? ''}
              />
            );
          case 'MemberEntriesFilter':
            return (
              <MemberEntriesFilter
                onValueChange={handleEntriesFilterChange}
                placeholder={filter.placeholder}
                value={(value as string) ?? ''}
              />
            );
          case 'PrizepicksDatePicker':
            return (
              <PrizepicksDatePicker
                calendarClassName="!z-[1001]"
                calendarPortalId="addlFiltersPicker"
                disabled={false}
                handleChange={(searchValue) => onChange(searchValue)}
                minDate={undefined}
                open={joinDatePickerOpen}
                placeholder="Join Date"
                setOpen={setJoinDatePickerOpen}
                value={(value as Date) ?? undefined}
              />
            );
          default:
            return (
              <Searchbar
                handleSearch={(searchValue) => onChange(searchValue)}
                placeholder={filter.placeholder}
                searchClassName="text-soClean-100 placeholder:text-soClean-180"
                searchValue={(value as string) ?? ''}
              />
            );
        }
      },
      [handleEntriesFilterChange, joinDatePickerOpen]
    );

    return (
      <Fragment>
        <p className="text-soClean-180 m-0 w-full text-sm leading-none">
          {groupName}
        </p>
        {filters.map((filter) => (
          <FormFieldController
            key={filter.name}
            containerClassName={filter.containerClassName}
            name={filter.name}
            control={control}
          >
            {({ value, onChange }) =>
              renderFilterComponent(filter, value, onChange)
            }
          </FormFieldController>
        ))}
        {!isLastGroup && <div className="bg-soFresh-130 my-2 h-px w-full" />}
      </Fragment>
    );
  }
);
